import React, { useState } from 'react';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';
import Button from '../../../components/ui/Button';


const PreferencesTab = () => {
  const [preferences, setPreferences] = useState({
    defaultExportFormat: 'mp4',
    defaultResolution: '1080p',
    defaultFrameRate: '24fps',
    creditStyle: 'modern',
    autoSave: true,
    showTutorials: true,
    darkMode: false,
    language: 'en',
    timezone: 'America/New_York'
  });

  const [loading, setLoading] = useState(false);

  const handlePreferenceChange = (key, value) => {
    setPreferences(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const handleSavePreferences = async () => {
    setLoading(true);
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 1500));
    setLoading(false);
  };

  const exportFormatOptions = [
    { value: 'mp4', label: 'MP4 (Recommended)' },
    { value: 'mov', label: 'MOV (QuickTime)' },
    { value: 'avi', label: 'AVI' },
    { value: 'webm', label: 'WebM' }
  ];

  const resolutionOptions = [
    { value: '720p', label: '720p HD' },
    { value: '1080p', label: '1080p Full HD' },
    { value: '1440p', label: '1440p 2K' },
    { value: '2160p', label: '2160p 4K' }
  ];

  const frameRateOptions = [
    { value: '23.976fps', label: '23.976 fps (Cinema)' },
    { value: '24fps', label: '24 fps (Film)' },
    { value: '25fps', label: '25 fps (PAL)' },
    { value: '29.97fps', label: '29.97 fps (NTSC)' },
    { value: '30fps', label: '30 fps' },
    { value: '60fps', label: '60 fps' }
  ];

  const creditStyleOptions = [
    { value: 'classic', label: 'Classic Hollywood' },
    { value: 'modern', label: 'Modern Minimal' },
    { value: 'elegant', label: 'Elegant Serif' },
    { value: 'bold', label: 'Bold Sans-Serif' },
    { value: 'indie', label: 'Independent Film' }
  ];

  const languageOptions = [
    { value: 'en', label: 'English' },
    { value: 'es', label: 'Español' },
    { value: 'fr', label: 'Français' },
    { value: 'de', label: 'Deutsch' },
    { value: 'it', label: 'Italiano' }
  ];

  const timezoneOptions = [
    { value: 'America/New_York', label: 'Eastern Time (ET)' },
    { value: 'America/Chicago', label: 'Central Time (CT)' },
    { value: 'America/Denver', label: 'Mountain Time (MT)' },
    { value: 'America/Los_Angeles', label: 'Pacific Time (PT)' },
    { value: 'Europe/London', label: 'Greenwich Mean Time (GMT)' },
    { value: 'Europe/Paris', label: 'Central European Time (CET)' }
  ];

  return (
    <div className="space-y-8">
      {/* Export Defaults */}
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-lg font-semibold text-foreground">Export Defaults</h3>
            <p className="text-sm text-muted-foreground">Set your preferred export settings for new projects</p>
          </div>
          <Button
            variant="default"
            loading={loading}
            onClick={handleSavePreferences}
            iconName="Save"
            iconPosition="left"
          >
            Save Preferences
          </Button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <Select
            label="Default Export Format"
            options={exportFormatOptions}
            value={preferences?.defaultExportFormat}
            onChange={(value) => handlePreferenceChange('defaultExportFormat', value)}
            description="File format for video exports"
          />

          <Select
            label="Default Resolution"
            options={resolutionOptions}
            value={preferences?.defaultResolution}
            onChange={(value) => handlePreferenceChange('defaultResolution', value)}
            description="Video resolution quality"
          />

          <Select
            label="Default Frame Rate"
            options={frameRateOptions}
            value={preferences?.defaultFrameRate}
            onChange={(value) => handlePreferenceChange('defaultFrameRate', value)}
            description="Frames per second"
          />
        </div>
      </div>
      {/* Credit Styling */}
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="mb-6">
          <h3 className="text-lg font-semibold text-foreground mb-2">Credit Styling</h3>
          <p className="text-sm text-muted-foreground">Choose your preferred credit appearance and formatting</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Select
            label="Default Credit Style"
            options={creditStyleOptions}
            value={preferences?.creditStyle}
            onChange={(value) => handlePreferenceChange('creditStyle', value)}
            description="Typography and layout style"
          />

          <div className="space-y-4">
            <h4 className="font-medium text-foreground">Style Preview</h4>
            <div className="bg-muted rounded-lg p-4 text-center">
              <div className="space-y-2">
                <div className={`text-lg font-semibold ${
                  preferences?.creditStyle === 'classic' ? 'font-serif' :
                  preferences?.creditStyle === 'elegant' ? 'font-serif' :
                  preferences?.creditStyle === 'bold'? 'font-bold' : 'font-medium'
                }`}>
                  DIRECTED BY
                </div>
                <div className={`text-base ${
                  preferences?.creditStyle === 'classic' ? 'font-serif' :
                  preferences?.creditStyle === 'elegant' ? 'font-serif' :
                  preferences?.creditStyle === 'bold'? 'font-semibold' : 'font-normal'
                }`}>
                  John Doe
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Interface Preferences */}
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="mb-6">
          <h3 className="text-lg font-semibold text-foreground mb-2">Interface Preferences</h3>
          <p className="text-sm text-muted-foreground">Customize your RealRoll experience</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-6">
            <Select
              label="Language"
              options={languageOptions}
              value={preferences?.language}
              onChange={(value) => handlePreferenceChange('language', value)}
              description="Interface language"
            />

            <Select
              label="Time Zone"
              options={timezoneOptions}
              value={preferences?.timezone}
              onChange={(value) => handlePreferenceChange('timezone', value)}
              description="For scheduling and timestamps"
            />
          </div>

          <div className="space-y-6">
            <div className="space-y-4">
              <h4 className="font-medium text-foreground">Behavior Settings</h4>
              
              <Checkbox
                label="Auto-save projects"
                description="Automatically save your work every 2 minutes"
                checked={preferences?.autoSave}
                onChange={(e) => handlePreferenceChange('autoSave', e?.target?.checked)}
              />

              <Checkbox
                label="Show tutorial hints"
                description="Display helpful tips and guidance"
                checked={preferences?.showTutorials}
                onChange={(e) => handlePreferenceChange('showTutorials', e?.target?.checked)}
              />

              <Checkbox
                label="Dark mode"
                description="Use dark theme for the interface"
                checked={preferences?.darkMode}
                onChange={(e) => handlePreferenceChange('darkMode', e?.target?.checked)}
              />
            </div>
          </div>
        </div>
      </div>
      {/* Template Favorites */}
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="mb-6">
          <h3 className="text-lg font-semibold text-foreground mb-2">Template Favorites</h3>
          <p className="text-sm text-muted-foreground">Quick access to your most-used credit templates</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[
            { name: 'Feature Film Standard', type: 'End Credits', lastUsed: '2 days ago' },
            { name: 'Documentary Style', type: 'Title Cards', lastUsed: '1 week ago' },
            { name: 'Short Film Minimal', type: 'End Credits', lastUsed: '3 days ago' }
          ]?.map((template, index) => (
            <div key={index} className="border border-border rounded-lg p-4 hover:bg-muted/50 transition-micro">
              <div className="flex items-start justify-between mb-2">
                <h4 className="font-medium text-foreground">{template?.name}</h4>
                <Button variant="ghost" size="sm" iconName="Star" className="text-warning" />
              </div>
              <p className="text-sm text-muted-foreground mb-1">{template?.type}</p>
              <p className="text-xs text-muted-foreground">Last used {template?.lastUsed}</p>
            </div>
          ))}
        </div>

        <div className="mt-4 pt-4 border-t border-border">
          <Button variant="outline" iconName="Plus" iconPosition="left">
            Add Current Template to Favorites
          </Button>
        </div>
      </div>
    </div>
  );
};

export default PreferencesTab;